import React, { useCallback, useEffect, useState, useMemo } from 'react';
import PivotGrid, {
    FieldChooser,
    Export,
    Scrolling,
    FieldPanel,
} from 'devextreme-react/pivot-grid';
import { loadMessages, locale } from "devextreme/localization";
import ruMessages from 'devextreme/localization/messages/ru.json';
import PivotGridDataSource from 'devextreme/ui/pivot_grid/data_source';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver-es';
import { exportDataGrid, exportPivotGrid } from 'devextreme/excel_exporter';
import Switch from 'devextreme-react/switch';
import { Lookup } from "devextreme-react/lookup";
import { Button } from "devextreme-react/button";
import { FilterRow, HeaderFilter, LoadPanel, Paging, SearchPanel } from "devextreme-react/data-grid";
import DataGrid, {
    GroupPanel, Column, Summary, GroupItem, TotalItem,
} from 'devextreme-react/data-grid';
import './upload.scss';
import { alert } from "devextreme/ui/dialog";
import { region } from '../../data/regions';

// Константы для выпадающих списков
const REPORT_TYPES = [
    { label: "Публикация фактического меню", value: 0 },
    { label: "Соответствие типового и фактического меню", value: 1 },
    { label: "Ошибки в фактическом меню", value: 2 },
    { label: "Одинаковые ссылки у пищеблоков в регионе", value: 3 },
    { label: "Пустые фактические меню (или с нарушенным шаблоном)", value: 4 },
    { label: "Даты типового меню, календаря питания, род.контроля", value: 5 },
    { label: `Список пищеблоков без факт. меню за ${new Date().toLocaleDateString('ru-RU')}`, value: 6 },
    { label: "Количество просмотров страниц пищеблока", value: 7 },
    { label: "Своевременность размещения фактического меню", value: 8 },
    { label: "Недочеты по мотивирующему мониторингу", value: 9 },
];

const YEARS = [
    { label: "2021", value: 2021 },
    { label: "2022", value: 2022 },
    { label: "2023", value: 2023 },
    { label: "2024", value: 2024 },
    { label: "2025", value: 2025 },
    { label: "2026", value: 2026 },
];

const MONTHS = [
    { label: "январь", value: "01" },
    { label: "февраль", value: "02" },
    { label: "март", value: "03" },
    { label: "апрель", value: "04" },
    { label: "май", value: "05" },
    { label: "июнь", value: "06" },
    { label: "июль", value: "07" },
    { label: "август", value: "08" },
    { label: "сентябрь", value: "09" },
    { label: "октябрь", value: "10" },
    { label: "ноябрь", value: "11" },
    { label: "декабрь", value: "12" },
];

export default function Upload() {
    // Локализация
    locale(sessionStorage.getItem('locale') || 'ru');
    loadMessages(ruMessages);

    // Состояния для фильтров
    const [selectedReport, setSelectedReport] = useState(6);
    const [selectedRegion, setSelectedRegion] = useState();
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(String(new Date().getMonth() + 1).padStart(2, "0"));
    
    // Состояния для отображения
    const [isBuilding, setIsBuilding] = useState(false);
    const [reportStatus, setReportStatus] = useState('Построить отчет');
    const [needsRefresh, setNeedsRefresh] = useState(true);
    const [expandedRows, setExpandedRows] = useState(false);
    
    // Данные отчетов
    const [pivotData, setPivotData] = useState([]);
    const [gridData, setGridData] = useState();

    // Обработчики изменения фильтров
    const handleFilterChange = useCallback(() => {
        setNeedsRefresh(true);
        setPivotData([]);
    }, []);

    const handleReportChange = useCallback((e) => {
        setSelectedReport(e.value);
        handleFilterChange();
    }, [handleFilterChange]);

    const handleRegionChange = useCallback((e) => {
        setSelectedRegion(e.value);
        handleFilterChange();
    }, [handleFilterChange]);

    const handleYearChange = useCallback((e) => {
        setSelectedYear(e.value);
        handleFilterChange();
    }, [handleFilterChange]);

    const handleMonthChange = useCallback((e) => {
        setSelectedMonth(e.value);
        handleFilterChange();
    }, [handleFilterChange]);

    const toggleExpandedRows = useCallback(() => {
        setExpandedRows(prev => !prev);
    }, []);

    // Источник данных для PivotGrid
    const pivotDataSource = useMemo(() => new PivotGridDataSource({
        fields: [
            {
                caption: 'Район',
                width: 120,
                dataField: 'Район',
                area: 'row',
                expanded: expandedRows,
            },
            {
                caption: 'Наименование',
                dataField: 'Наименование',
                width: 150,
                area: 'row',
            },
            {
                dataField: 'Дата',
                dataType: 'Дата',
                area: 'column',
            },
            {
                caption: 'Код',
                dataField: 'Код',
                area: 'data',
            }
        ],
        store: pivotData,
    }), [pivotData, expandedRows]);

    // Загрузка данных
    const fetchData = async (endpoint) => {
        try {
            const response = await fetch(endpoint);
            if (!response.ok) throw new Error('Ошибка загрузки данных');
            return await response.json();
        } catch (error) {
            alert('База данных недоступна. Попробуйте позже');
            console.error(error);
            return [];
        }
    };

    // Обработчик клика по ячейке PivotGrid
    const handleCellClick = useCallback((e) => {
        if (e.cell?.path?.length > 1) {
            const item = pivotData.find(item => item.Наименование === e.cell.path[1]);
            if (item) {
                window.open(`https://monitoring.cemon.ru/#/profile?id=${item.Код}`, '_blank').focus();
            }
        }
    }, [pivotData]);

    // Экспорт данных
    const handlePivotExport = useCallback((e) => {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Экспорт');

        exportPivotGrid({
            component: e.component,
            worksheet,
        }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Экспорт.xlsx');
            });
        });
    }, []);

    const handleGridExport = useCallback((e) => {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Выгрузка');

        exportDataGrid({
            component: e.component,
            worksheet,
            autoFilterEnabled: true,
        }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Экспорт.xlsx');
            });
        });
    }, []);

    // Генерация отчета
    const generateReport = useCallback(async () => {
        if (!selectedRegion) {
            alert('Пожалуйста, выберите регион');
            return;
        }

        setIsBuilding(true);
        setReportStatus('Загрузка данных...');
        setNeedsRefresh(false);

        try {
            const baseUrl = 'https://api.cemon.ru/otcheti/';
            const params = `?id=${selectedRegion}&m=${selectedMonth}&g=${selectedYear}`;

            switch (selectedReport) {
                case 0:
                    const data = await fetchData(`${baseUrl}menupub/${params}`);
                    setPivotData(data);
                    break;
                case 1:
                    setGridData(`${baseUrl}sootvmenu/${params}`);
                    break;
                case 2:
                    setGridData(`${baseUrl}menuoshib/${params}`);
                    break;
                case 3:
                    setGridData(`${baseUrl}dubllink/${params}`);
                    break;
                case 4:
                    setGridData(`${baseUrl}blankmenu/${params}`);
                    break;
                case 5:
                    setGridData(`${baseUrl}dati_tm_kp_rk/${params}`);
                    break;
                case 6:
                    setGridData(`${baseUrl}netmenusegodna/${params}`);
                    break;
                case 7:
                    setGridData(`${baseUrl}active/${params}`);
                    break;
                case 8:
                    setGridData(`${baseUrl}svorvrem/${params}`);
                    break;
                case 9:
                    setGridData(`${baseUrl}mmproblem/${params}`);
                    break;
                default:
                    break;
            }
        } finally {
            setIsBuilding(false);
            setReportStatus('Построить отчет');
        }
    }, [selectedReport, selectedRegion, selectedYear, selectedMonth]);

    // Компонент отчета
    const ReportComponent = useMemo(() => {
        if (needsRefresh) return null;

        if (selectedReport === 0) {
            return (
                <div>
                    <div>Развернуть значения <Switch value={expandedRows} onValueChanged={toggleExpandedRows} /></div>
                    <PivotGrid
                        dataSource={pivotDataSource}
                        allowSortingBySummary={true}
                        allowFiltering={true}
                        showBorders={true}
                        showColumnTotals={true}
                        showColumnGrandTotals={true}
                        showRowTotals={true}
                        showRowGrandTotals={true}
                        rowHeaderLayout={'tree'}
                        onExporting={handlePivotExport}
                        onCellClick={handleCellClick}
                        encodeHtml={false}
                    >
                        <FilterRow visible={true} />
                        <Export enabled={true} />
                        <FieldChooser enabled={true} height={400} />
                        <FieldPanel
                            showColumnFields={true}
                            showDataFields={false}
                            showFilterFields={false}
                            showRowFields={true}
                            allowFieldDragging={true}
                            visible={true}
                        />
                    </PivotGrid>
                    <div>SHIFT+колёсико мыши - горизонтальная прокрутка</div>
                </div>
            );
        }

        return (
            <div className={'dx-datagridd'}>
                <DataGrid
                    dataSource={gridData}
                    showBorders={true}
                    onExporting={handleGridExport}
                    wordWrapEnabled={true}
                    showColumnLines={true}
                >
                    <FilterRow visible={true} />
                    <SearchPanel visible={true} />
                    <HeaderFilter visible={true} />
                    <Paging pageSize={100} />
                    <Export enabled={true} />
                    <GroupPanel visible={true} />
                    <LoadPanel
                        height={100}
                        width={200}
                        indicatorSrc={require('./load.gif')}
                    />
                </DataGrid>
            </div>
        );
    }, [
        needsRefresh, selectedReport, pivotDataSource, expandedRows, 
        gridData, handlePivotExport, handleCellClick, toggleExpandedRows, 
        handleGridExport
    ]);

    return (
        <React.Fragment>
            <h2 className={'content-block'}>Отчёт</h2>

            <div className={'content-block'}>
                <div className={'dx-card responsive-paddings'} style={{ fontSize: '14px' }}>
                    <Lookup
                        label="Отчет"
                        labelMode='static'
                        stylingMode='outlined'
                        dataSource={REPORT_TYPES}
                        displayExpr="label"
                        valueExpr="value"
                        searchEnabled={false}
                        placeholder="Выбор отчета"
                        value={selectedReport}
                        onValueChanged={handleReportChange}
                    />
                    <Lookup
                        label="Регион"
                        labelMode='static'
                        stylingMode='outlined'
                        dataSource={region}
                        displayExpr="label"
                        valueExpr="value"
                        searchEnabled={true}
                        placeholder="Выбор региона"
                        onValueChanged={handleRegionChange}
                        value={selectedRegion}
                    />
                    <Lookup
                        label="Год"
                        labelMode='static'
                        stylingMode='outlined'
                        dataSource={YEARS}
                        displayExpr="label"
                        valueExpr="value"
                        searchEnabled={false}
                        placeholder="Выбор отчетного года"
                        onValueChanged={handleYearChange}
                        value={selectedYear}
                    />
                    <Lookup
                        label="Месяц"
                        labelMode='static'
                        stylingMode='outlined'
                        dataSource={MONTHS}
                        displayExpr="label"
                        valueExpr="value"
                        searchEnabled={false}
                        placeholder="Выбор отчетного месяца"
                        value={selectedMonth}
                        onValueChanged={handleMonthChange}
                    />
                    <Button
                        width={180}
                        text={reportStatus}
                        stylingMode="contained"
                        onClick={generateReport}
                        icon="search"
                        disabled={isBuilding}
                    />
                </div>

                <div className={'dx-card responsive-paddings'} style={{ fontSize: '14px' }}>
                    {ReportComponent}
                </div>
            </div>
        </React.Fragment>
    );
}